import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Search, Users, CheckCircle, Building2, Eye, Gavel } from 'lucide-react';
const stepsPrivate = [{
  number: 1,
  icon: FileText,
  title: 'Oddate podatke o vozilu',
  description: 'Kratek obrazec oddate v manj kot 2 minutah.'
}, {
  number: 2,
  icon: Search,
  title: 'Dogovorimo termin',
  description: 'Naš svetovalec z vami uskladi termin za pregled vozila.'
}, {
  number: 3,
  icon: Eye,
  title: 'Pregled vozila',
  description: 'Vozilo strokovno pregledamo in skupaj z vami določimo minimalno prodajno ceno.'
}, {
  number: 4,
  icon: Gavel,
  title: 'Dražba vozila',
  description: 'Vozilo umestimo v dražbo z več kot 5.000 dražitelji, ki jo lahko spremljate v živo.'
}];
const stepsBusiness = [{
  number: 1,
  icon: FileText,
  title: 'Pošljete podatke o vozilu',
  description: 'Posredujete podatke o vozilu in podjetju v nekaj minutah.'
}, {
  number: 2,
  icon: Search,
  title: 'Pripravimo cenitev in pogodbo',
  description: 'Naša ekipa pripravi cenitev in osnutek pogodbe za poslovno prodajo.'
}, {
  number: 3,
  icon: Users,
  title: 'Vozilo predstavimo kupcem',
  description: 'Vozilo predstavimo preverjeni mreži poslovnih kupcev in trgovcev.'
}, {
  number: 4,
  icon: CheckCircle,
  title: 'Uredimo poslovno transakcijo',
  description: 'Poskrbimo za vso dokumentacijo, račune in prenos lastništva.'
}];
export function HowItWorks() {
  const [activeTab, setActiveTab] = useState<'private' | 'business'>('private');
  const steps = activeTab === 'private' ? stepsPrivate : stepsBusiness;
  const scrollToForm = () => {
    document.getElementById('form')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section id="kako-deluje" className="py-16 md:py-24 bg-white" data-id="element-197">
      <div className="container mx-auto px-4" data-id="element-198">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-8" data-id="element-199">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-200">
            Kako deluje?
          </h2>
          <p className="text-lg text-slate-600" data-id="element-201">
            Preprost postopek v štirih korakih
          </p>
        </motion.div>

        {/* Tab Switcher */}

        <div className="max-w-5xl mx-auto" data-id="element-202">
          {/* Desktop Timeline */}
          <div className="hidden md:grid md:grid-cols-4 gap-8 mb-12" data-id="element-203">
            {steps.map((step, index) => <motion.div key={`${activeTab}-${step.number}`} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className="relative" data-id="element-204">
                {index < steps.length - 1 && <div className="absolute top-8 left-[60%] w-full h-0.5 bg-[#1EB6E8]/30 hidden md:block" data-id="element-205"></div>}
                <div className="relative z-10 flex flex-col items-center text-center" data-id="element-206">
                  <div className="w-16 h-16 bg-[#1EB6E8] rounded-full flex items-center justify-center text-white text-2xl font-bold mb-4 shadow-lg" data-id="element-207">
                    {step.number}
                  </div>
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-[#1EB6E8]/10 rounded-lg mb-3" data-id="element-208">
                    <step.icon className="w-6 h-6 text-[#1EB6E8]" data-id="element-209" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-210">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm" data-id="element-211">{step.description}</p>
                </div>
              </motion.div>)}
          </div>

          {/* Mobile Timeline */}
          <div className="md:hidden space-y-6 mb-12" data-id="element-212">
            {steps.map((step, index) => <motion.div key={`${activeTab}-mobile-${step.number}`} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.08
          }} className="flex gap-4" data-id="element-213">
                <div className="flex flex-col items-center" data-id="element-214">
                  <div className="w-12 h-12 bg-[#1EB6E8] rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0" data-id="element-215">
                    {step.number}
                  </div>
                  {index < steps.length - 1 && <div className="w-0.5 h-full bg-[#1EB6E8]/30 mt-2" data-id="element-216"></div>}
                </div>
                <div className="flex-1 pb-6" data-id="element-217">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-[#1EB6E8]/10 rounded-lg mb-2" data-id="element-218">
                    <step.icon className="w-5 h-5 text-[#1EB6E8]" data-id="element-219" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-220">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm" data-id="element-221">{step.description}</p>
                </div>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center" data-id="element-222">
            <div className="flex flex-col items-center gap-2 mb-6" data-id="element-223">
              {['Vozila niste dolžni prodati, če ponudba ni ustrezna.', 'Vse aktivnosti v zvezi s prodajo prevzame ekipa Autopoint.', 'Denar prejmete takoj.'].map(item => <div key={item} className="flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full border border-emerald-200" data-id="element-224">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" data-id="element-225" />
                  <span className="text-emerald-800 text-sm font-medium" data-id="element-226">
                    {item}
                  </span>
                </div>)}
            </div>
            <button onClick={scrollToForm} className="px-8 py-3 bg-[#1EB6E8] text-white rounded-md font-medium hover:bg-[#1aa3d1] transition-colors" data-id="element-227">
              Prodaj vozilo
            </button>
          </motion.div>
        </div>
      </div>
    </section>;
}